import React, { useState, useEffect } from "react";

import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";

interface Store {
  id: number;
  state: string;
  name: string;
  address_street_1?: string;
  address_street_2?: string;
  city?: string;
  postcode?: string | number;
  latitude: any;
  longitude: any;
  distance: string;
  hours: string;
  phone: string;
  email: string;
  services: { name: string; icon: string }[];
  store_closing_hour?: string;
}

interface StoreFinderListProps {
  showList: boolean;
  storesData: any;
  onSelectStore: (store: Store) => void;
  hasManualSearch: boolean;
}

const stateMapping: Record<string, string> = {
  ALL: "ALL",
  ACT: "Australian Capital Territory",
  NSW: "New South Wales",
  QLD: "Queensland",
  SA: "South Australia",
  TAS: "Tasmania",
  VIC: "Victoria",
  WA: "Western Australia",
};

const states = Object.keys(stateMapping);

const StoreFinderList = ({ showList, storesData, onSelectStore, hasManualSearch }: StoreFinderListProps) => {
  // console.log('storesData', storesData)
  const [selectedState, setSelectedState] = useState<string>("ALL");
  const isMobile = useMobileBreakpoint("1024px");

  const normalizeState = (state: string) => {
    const entry = Object.entries(stateMapping).find(([abbr, full]) => full === state || abbr === state);
    return entry ? entry[0] : "UNKNOWN";
  };

  // Normalise store states and filter out stores where sfs_enabled === 0
  const normalisedStores = storesData
    .filter((store) => {
      return store.clickandcollect_enabled === 1 || store.sfs_enabled === 1 || store.deptstore_enabled === 1
    })
    .map((store) => ({
      ...store,
      state: normalizeState(store.state),
    }));
  // console.log('normalisedStores', normalisedStores)

  // Filter stores based on the selected state
  const filteredStores: Store[] =
  selectedState === "ALL"
    ? normalisedStores
    : normalisedStores.filter((store) => store.state === selectedState);

  // Group stores by normalised state
  const groupedStores: Record<string, Store[]> = filteredStores.reduce(
    (acc: Record<string, Store[]>, store) => {
      if (!acc[store.state]) acc[store.state] = [];
      acc[store.state].push(store);
      return acc;
    },
    {}
  );

  return (
    <div className="max-lg:pl-4">
      {hasManualSearch && (
        <div className="flex justify-between items-center py-4 mr-4 border-b border-black text-[#54565A] lg:mx-8 lg:mr-0">
          <span className="b1">Stores found</span>
          <span className="b1">{filteredStores.length}</span>
        </div>
      )}

      {!hasManualSearch && (
        <div className="flex overflow-auto lg:pl-6">
          {states.map((state) => (
            <button
              key={state}
              className={`uppercase bc font-medium border-b-2 px-6 ${
                selectedState === state ? "border-[#54565A] text-black" : "text-[#99A8B2] border-transparent"
              }`}
              onClick={() => setSelectedState(state)}
            >
              {state}
            </button>
          ))}
        </div>
      )}
      
      {(showList || !isMobile) &&
        <div className="pr-4 pt-5 lg:p-6 lg:h-[1086px] lg:overflow-auto lg:w-[640px]">
          {Object.entries(groupedStores).map(([state, stores]) => (
            <div key={state}>
              <span className="bc font-medium text-[#54565A] uppercase mb-3 block lg:px-2 lg:mb-4">
                {stateMapping[state] || state}
              </span>
              {stores.map((store) => (
                <div
                  key={store.id}
                  className="flex justify-between gap-4 lg:px-2 pt-1 pb-3 my-3 border-b border-b-[#BBBBBD] cursor-pointer hover:bg-[#ECEEEF] lg:pt-2 lg:pb-4 lg:my-4 lg:gap-2"
                  onClick={() => onSelectStore(store)}
                >
                  <div>
                    <h3 className="mb-2 font-normal text-black">
                      {store.name}
                    </h3>
                    <p className="text-black b3 mb-2 lg:max-w-[250px]">
                      {store.address_street_1}<br />
                      {store.address_street_2 && <>{store.address_street_2}<br /></>}
                      {store.city} {store.state} {store.postcode}
                    </p>
                    <ul className="flex gap-4 p-0">
                      <li className="list-none"><i className="icon icon-tailor"></i></li>
                      <li className="list-none"><i className="icon icon-hanger"></i></li>
                      <li className="list-none"><i className="icon icon-scissors"></i></li>
                      <li className="list-none"><i className="icon icon-fabric"></i></li>
                      <li className="list-none"><i className="icon icon-measure"></i></li>
                    </ul>
                  </div>
                  <div className="flex flex-col b3 font-normal text-[#54565A] flex-none">
                    <span className="text-right">
                      {store.distance && hasManualSearch ? `${store.distance}km` : ''}
                    </span>
                    <span className="hidden lg:block">
                      {store?.store_closing_hour}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      }
    </div>
  );
};

export default StoreFinderList;